// ── GameLinesSection — NBA period "Game Lines" table ─────────────────
// A SEPARATE, additive section on the Game Detail page; the full-game
// Moneyline / Spread / Total sections are untouched. Renders period bets
// through the SAME GameRows component the moneyline table uses — identical
// columns + paired LadderRow orderbook ladder on expand — just with the Time
// (Score) column hidden. Layout: tabs + section toolbar → bet-type segmented
// tabs → period segmented tabs → column header → GameRows.
//
// Periods: 1st Half (Kalshi + Poly) + single quarters 1Q–4Q (Kalshi-only —
// Polymarket lists no quarter markets, so quarter rows show "—" for Poly; that
// is expected, not a bug). The period tabs are data-driven (enabled only where
// the active bet type has markets). Moneyline is 1H-only — there is no quarter
// winner — so quarter tabs stay disabled on the Moneyline sub-tab.
//
// Data: getAvailablePeriods (which tabs to enable), getGameEvents(…, period)
// for spread/total, getPeriodMoneyline('1H') for the 3-way (Away/Home/Tie)
// winner. Period markets share the full-game canonicalGameKey but are bucketed
// by period, so the full-game rows are never affected (the 0cf7549 invariant).
// A quarter resolves on that quarter alone — 2Q ≠ 1H — never paired across.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMarketStore, TERMINAL_STATUSES } from '@/stores/marketStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { Th } from '@/components/atoms/TableCells';
import { GameRows } from '@/views/ValueDashboard/GameTable';
import { SectionToolbar, Chip, ChipGroup } from './SectionToolbar';
import { synthesizeFromSpread, synthesizeFromTotal } from '@/views/SpreadsTotals/synthesize';
import { buildBetLineContext } from '@/views/SpreadsTotals/buildBetLineContext';
import { useUserKalshiPOS } from '@/hooks/useUserKalshiPOS';
import type { ActiveSpreadTotal } from './SpreadTotalSection';
import type { GameRow, GameEventRow, MarketSide, Period, PriceMode, TeamSide, VenueBook } from '@/views/ValueDashboard/types';
import type { TradeTicketContext } from '@/views/ValueDashboard/TradeTicketBody';

type BetType = 'spread' | 'total' | 'moneyline';

const BET_TYPES: ReadonlyArray<{ id: BetType; label: string }> = [
  { id: 'spread', label: 'Spread' },
  { id: 'total', label: 'Point Total' },
  { id: 'moneyline', label: 'Moneyline' },
];
// Selectable periods (data-driven enable below). '2nd Half' has no markets and
// stays a static greyed placeholder.
const PERIODS: ReadonlyArray<{ id: Period; label: string }> = [
  { id: '1H', label: '1st Half' },
  { id: '1Q', label: '1st Quarter' },
  { id: '2Q', label: '2nd Quarter' },
  { id: '3Q', label: '3rd Quarter' },
  { id: '4Q', label: '4th Quarter' },
];

interface Props {
  /** Full-game GameRow (carries codes / names / sport). Period lines hang off
   *  the same canonicalGameKey (= game.key). */
  game: GameRow;
  /** Deep-link focus from the Positions page (`?ticker=&side=`). When the
   *  ticker is an NBA period market for this game, the bet-type + period tabs
   *  jump to it, the line expands, and the trade ticket seeds. Ignored
   *  otherwise (full-game tickers are handled by the other sections). */
  focusTicker?: string | null;
  focusSide?: 'yes' | 'no';
  /** Re-seeds the trade ticket. Same handler the full-game sections use. */
  onPillClick: (ctx: TradeTicketContext) => void;
  /** Emits the active spread/total line's bet-line context (synth game +
   *  "Over X" / "TEAM wins by over X" title + line strip) so GameDetail's
   *  right-side panel renders the same over-only/YES-NO layout the full-game
   *  SpreadTotalSection produces. Null on a moneyline selection. */
  onActiveLineChange?: (active: ActiveSpreadTotal | null) => void;
  /** Which spread/total line owns the panel — PARENT-controlled (mirrors
   *  SpreadTotalSection's activeKind) so the parent can release it when the
   *  middle table or a moneyline takes over the panel, stopping this section's
   *  emit from clobbering. Null = no active line / moneyline. */
  panelLine?: { kind: 'spread' | 'total'; idx: number } | null;
  onPanelLineChange?: (line: { kind: 'spread' | 'total'; idx: number } | null) => void;
}

/** One renderable bet = a synth GameRow + how GameRows should show it. */
interface BetRow {
  synth: GameRow;
  /** undefined → away/home pair (binary); set → single-outcome row. */
  singleSide?: TeamSide;
  /** This row continues the previous group (the 1H Tie under the away/home
   *  pair) — rendered with the pair's gameIndex so all three outcomes share
   *  one zebra background, and without the game-boundary top border. */
  continuation?: boolean;
}

/** A row is "settled" when every venue book with a known market status is
 *  terminal (TERMINAL_STATUSES — closed/settled/finalized/determined) and at
 *  least one status is known. Quarter bets resolve mid-game, so a live game's
 *  table mixes settled and working lines: settled rows stay visible with a
 *  SETTLED chip by default (label, don't hide — Brendan Jun 11) and the
 *  "Hide settled" toggle drops them for a working-lines-only view. Reads
 *  marketStore imperatively; the component's `version` subscription keeps
 *  the result fresh across status flips. */
function rowIsSettled(synth: GameRow): boolean {
  const store = useMarketStore.getState();
  const statuses: string[] = [];
  const add = (b: VenueBook | null | undefined) => {
    const status = b?.marketTicker ? store.getMarket(b.marketTicker)?.status : null;
    if (status) statuses.push(status);
  };
  add(synth.kalshi?.away);
  add(synth.kalshi?.home);
  add(synth.polymarket?.away);
  add(synth.polymarket?.home);
  return statuses.length > 0 && statuses.every((st) => TERMINAL_STATUSES.has(st));
}

/** A row has "real" volume when any of its venue books has traded contracts.
 *  Zero across the board = an empty placeholder market (e.g. Polymarket's
 *  2/98, 0-volume tail strikes) — hidden by the "Hide 0-Vol" toggle. */
function rowHasVolume(synth: GameRow): boolean {
  const traded = (b: VenueBook | null | undefined) => (b?.volume ?? 0) > 0;
  return (
    traded(synth.kalshi?.away) ||
    traded(synth.kalshi?.home) ||
    traded(synth.polymarket?.away) ||
    traded(synth.polymarket?.home)
  );
}

export function GameLinesSection({ game, focusTicker, focusSide, onPillClick, onActiveLineChange, panelLine = null, onPanelLineChange }: Props) {
  const version = useMarketStore((s) => s.version);
  // Inherit the user's defaults exactly like MoneylineSection / SpreadTotalSection:
  // price mode from displaySettings; side = 'no' (the app-wide convention — there's
  // no persisted YES/NO preference). Hydra now streams the 1H books live over WS, so
  // Maker no longer lags here and there's no reason to override the price mode.
  const defaultPriceMode = useDisplaySettings((s) => s.defaultPriceMode);
  const [priceMode, setPriceMode] = useState<PriceMode>(defaultPriceMode);
  const [marketSide, setMarketSide] = useState<MarketSide>('no');
  const [betType, setBetType] = useState<BetType>('spread');
  const [expanded, setExpanded] = useState<{ key: string; side: TeamSide } | null>(null);
  // "Hide 0-Vol": drop rows whose markets have never traded (volume 0). On by
  // default to cut the empty placeholder lines (e.g. Poly's 2/98 tail strikes);
  // toggle off to see every open market, traded or not.
  const [hideNoVol, setHideNoVol] = useState(true);
  // "Hide settled": resolved rows are LABELED by default (SETTLED chip,
  // dimmed) rather than removed — traders wanted settlement prices visible
  // next to working lines. Toggle on to drop them entirely.
  const [hideSettled, setHideSettled] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState<Period>('1H');

  // Sports with period markets in this table: NBA (1H + quarters) and WNBA
  // (1H only — Kalshi lists no WNBA quarters). Other sports render nothing here
  // (their full-game lines live in the top Moneyline / Spread-Total sections).
  const supportsPeriods = game.sport === 'nba' || game.sport === 'wnba';

  // Which non-full periods have data, by bet type (one cheap scan). Drives the
  // period tabs + bet-type availability. Data-driven, so WNBA naturally shows
  // only 1H (no quarter markets ⇒ no quarter tabs).
  const avail = useMemo(() => {
    void version;
    const empty = { spread: [] as Period[], total: [] as Period[], moneyline: [] as Period[] };
    if (!supportsPeriods) return empty;
    return useMarketStore.getState().getAvailablePeriods(game.key);
  }, [game.key, supportsPeriods, version]);

  const hasSpread = avail.spread.length > 0;
  const hasTotal = avail.total.length > 0;
  const hasMoneyline = avail.moneyline.length > 0;
  const availableBets: BetType[] = [
    ...(hasSpread ? (['spread'] as const) : []),
    ...(hasTotal ? (['total'] as const) : []),
    ...(hasMoneyline ? (['moneyline'] as const) : []),
  ];
  const effectiveBetType: BetType = availableBets.includes(betType) ? betType : availableBets[0] ?? 'spread';

  // Periods valid for the active bet type. Moneyline is 1H-only (Kalshi lists
  // no quarter winner), so quarter tabs stay disabled there.
  const availForBet: Period[] =
    effectiveBetType === 'spread' ? avail.spread : effectiveBetType === 'total' ? avail.total : avail.moneyline;
  const effectivePeriod: Period = availForBet.includes(selectedPeriod) ? selectedPeriod : availForBet[0] ?? '1H';

  // Spread/total lines for the active period (Kalshi-anchored; quarters have an
  // empty Poly column by design). Moneyline (1H-only) comes from getPeriodMoneyline.
  const event = useMemo<GameEventRow | null>(() => {
    void version;
    if (!supportsPeriods) return null;
    return (
      useMarketStore
        .getState()
        .getGameEvents(game.sport, { dateFilter: 'all', statusFilter: 'all' }, effectivePeriod)
        .find((e) => e.key === game.key) ?? null
    );
  }, [game.key, game.sport, supportsPeriods, effectivePeriod, version]);

  const moneyline = useMemo(() => {
    void version;
    if (!supportsPeriods) return null;
    return useMarketStore.getState().getPeriodMoneyline(game.key, '1H');
  }, [game.key, supportsPeriods, version]);

  // Subscribe depth for just the tickers shown (the active period's lines + the
  // 1H winner when moneyline is up). PosCell + ladders read these.
  const orderbookTickers = useMemo<string[]>(() => {
    const set = new Set<string>();
    const add = (b: VenueBook | null | undefined) => { if (b?.marketTicker) set.add(b.marketTicker); };
    for (const s of event?.spreads ?? []) { add(s.kalshi); add(s.polymarket); }
    for (const t of event?.totals ?? []) { add(t.kalshi); add(t.polymarket); }
    if (moneyline) {
      add(moneyline.away.kalshi); add(moneyline.away.polymarket);
      add(moneyline.home.kalshi); add(moneyline.home.polymarket);
      add(moneyline.tie.kalshi);
    }
    return Array.from(set);
  }, [event, moneyline]);
  const tickersKey = orderbookTickers.slice().sort().join('|');

  // Per-user Kalshi positions + resting orders — drives PosCell (position ring
  // + open-order chips) and the ladder's resting-order chips. Kalshi-only.
  const kalshiTickers = useMemo(
    () => orderbookTickers.filter((t) => !t.startsWith('PM-')),
    [orderbookTickers],
  );
  const { positionByTicker, ordersByTicker } = useUserKalshiPOS(kalshiTickers);
  useEffect(() => {
    const viewId = `game-lines:${game.key}`;
    useMarketStore.getState().setOrderbookNeeds(viewId, orderbookTickers);
    return () => useMarketStore.getState().setOrderbookNeeds(viewId, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.key, tickersKey]);

  // ── build the bet rows for the active bet-type × period ─────────────
  const rows: BetRow[] = [];
  if (effectiveBetType === 'spread' && event) {
    for (const line of event.spreads) {
      const synth = synthesizeFromSpread(event, line);
      // Bet label as a sentence using the team CODE: "NYK wins by over 7.5".
      // We render only the favored side (= the YES outcome, covers the line),
      // so override just that side's name; synthesizeFromSpread's default is
      // the abbreviated "NYK −7.5" form used by the full-game section.
      const favCode = line.favored === 'away' ? event.awayCode : event.homeCode;
      const label = `${favCode} wins by over ${Math.abs(line.line)}`;
      if (line.favored === 'away') synth.awayName = label;
      else synth.homeName = label;
      rows.push({ synth, singleSide: line.favored });
    }
  } else if (effectiveBetType === 'total' && event) {
    for (const line of event.totals) {
      rows.push({ synth: synthesizeFromTotal(event, line), singleSide: 'away' }); // away = Over
    }
  } else if (effectiveBetType === 'moneyline' && moneyline) {
    const ml = moneyline;
    const aName = game.awayName || game.awayCode;
    const hName = game.homeName || game.homeCode;
    if (ml.away.kalshi && ml.home.kalshi) {
      rows.push({
        synth: {
          ...game,
          key: `${game.key}|1h-ml`,
          gameState: 'pre',
          finalWinnerSide: null,
          awayName: `${aName} (1H)`,
          homeName: `${hName} (1H)`,
          kalshi: { away: ml.away.kalshi, home: ml.home.kalshi },
          polymarket: ml.away.polymarket && ml.home.polymarket ? { away: ml.away.polymarket, home: ml.home.polymarket } : null,
        },
      });
    }
    if (ml.tie.kalshi) {
      rows.push({
        synth: {
          ...game,
          key: `${game.key}|1h-ml-tie`,
          gameState: 'pre',
          finalWinnerSide: null,
          awayName: 'Tie (1H)',
          homeName: 'Tie (1H)',
          kalshi: { away: ml.tie.kalshi, home: ml.tie.kalshi },
          polymarket: null,
        },
        singleSide: 'away',
        // Same background as the Away/Home rows above — the three outcomes
        // read as one 3-way market, not two zebra-striped games.
        continuation: rows.length > 0,
      });
    }
  }

  // ── Deep-link focus (Positions page ?ticker=&side=) ─────────────────
  // A period bet's row lives here, not in the full-game sections, so Game
  // Lines claims the focus. Phase 1: point the bet-type + period tabs at the
  // ticker. Phase 2: once that line's row is built, expand it + seed the
  // trade ticket. GameDetail skips its full-game default seed for period
  // tickers so this isn't clobbered.
  const pendingFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusTicker) return;
    const m = focusTicker.toUpperCase().match(/^KXW?NBA(1H|[1-4]Q)(SPREAD|TOTAL|WINNER)/);
    if (!m) return; // not an NBA/WNBA period ticker — the full-game sections own it
    setBetType(m[2] === 'SPREAD' ? 'spread' : m[2] === 'TOTAL' ? 'total' : 'moneyline');
    setSelectedPeriod(m[1] as Period);
    if (focusSide) setMarketSide(focusSide);
    pendingFocusRef.current = focusTicker.toUpperCase();
  }, [focusTicker, focusSide]);
  useEffect(() => {
    const ft = pendingFocusRef.current;
    if (!ft) return;
    const target = rows.find(
      (r) => r.synth.kalshi?.away.marketTicker === ft || r.synth.kalshi?.home.marketTicker === ft,
    );
    if (!target) return; // target period's rows not built yet — re-runs when they are
    pendingFocusRef.current = null;
    const side: TeamSide =
      target.synth.kalshi?.home.marketTicker === ft && target.synth.kalshi?.away.marketTicker !== ft
        ? 'home'
        : 'away';
    setExpanded({ key: target.synth.key, side });
    onPanelLineChange?.(
      effectiveBetType === 'spread' || effectiveBetType === 'total'
        ? { kind: effectiveBetType, idx: rows.indexOf(target) }
        : null,
    );
    onPillClick({
      game: target.synth,
      teamSide: side,
      venue: 'kalshi',
      initialOrderSide: focusSide ?? marketSide,
      postOnly: priceMode === 'maker',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Emit the active spread/total line's bet-line context to the right-side
  // panel — same mechanism SpreadTotalSection uses, so the order entry reads
  // identically (an "Over X" / "TEAM wins by over X" title + line strip with a
  // single YES/NO, not a team-vs-team header). Re-runs as `event` ticks so the
  // panel stays live. Null on a moneyline selection (team-style panel).
  useEffect(() => {
    if (!onActiveLineChange) return;
    if (!panelLine || !event) {
      onActiveLineChange(null);
      return;
    }
    if (panelLine.kind === 'spread') {
      const line = event.spreads[panelLine.idx];
      if (!line) { onActiveLineChange(null); return; }
      onActiveLineChange({
        game: synthesizeFromSpread(event, line),
        context: buildBetLineContext({
          kind: 'spread', event, lines: event.spreads, selectedIdx: panelLine.idx,
          onSelectIdx: (i) => onPanelLineChange?.({ kind: 'spread', idx: i }),
        }),
      });
    } else {
      const line = event.totals[panelLine.idx];
      if (!line) { onActiveLineChange(null); return; }
      onActiveLineChange({
        game: synthesizeFromTotal(event, line),
        context: buildBetLineContext({
          kind: 'total', event, lines: event.totals, selectedIdx: panelLine.idx,
          onSelectIdx: (i) => onPanelLineChange?.({ kind: 'total', idx: i }),
        }),
      });
    }
  }, [panelLine, event, onActiveLineChange, onPanelLineChange]);

  // Visibility resolved up front so the 1H moneyline pair + Tie can
  // coordinate as ONE selection group: whichever of them is expanded, the
  // blue box wraps both (group top rail on the pair, bottom rail on the
  // Tie). Spread/total rows are independent bets and never group.
  const visibleRows = rows.map((r) => {
    const settled = rowIsSettled(r.synth);
    const visible = !(hideNoVol && !rowHasVolume(r.synth)) && !(hideSettled && settled);
    return { ...r, settled, visible };
  });
  const mlPair = effectiveBetType === 'moneyline' ? visibleRows.find((r) => !r.continuation) : undefined;
  const mlTie = effectiveBetType === 'moneyline' ? visibleRows.find((r) => r.continuation) : undefined;
  const mlPairExpanded = !!mlPair?.visible && expanded?.key === mlPair.synth.key;
  const mlTieExpanded = !!mlTie?.visible && expanded?.key === mlTie.synth.key;

  if (!hasSpread && !hasTotal && !hasMoneyline) return null;

  return (
    <section className="bg-card border-border rounded-lg border">
      {/* Title + section toolbar (Maker/Taker/Market + YES/NO). Player Props /
          Events are hidden until those surfaces are actually built. */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <span className="text-foreground text-lg font-bold">Game Lines</span>
        <SectionToolbar priceMode={priceMode} onPriceMode={setPriceMode} marketSide={marketSide} onMarketSide={setMarketSide} />
      </div>

      {/* Bet-type + period selectors — button style (matches the Maker/Taker
          toolbar). Only AVAILABLE options render: bet types with markets, and
          for the active bet type the periods that have data (so Moneyline shows
          just 1H, no quarters; Overtime / 2nd Half never appear). */}
      <div className="border-border flex flex-wrap items-center gap-3 border-b px-4 py-2">
        <ChipGroup>
          {BET_TYPES.filter((bt) => availableBets.includes(bt.id)).map((bt) => (
            <Chip key={bt.id} active={effectiveBetType === bt.id} onClick={() => setBetType(bt.id)}>
              {bt.label}
            </Chip>
          ))}
        </ChipGroup>
        {availForBet.length > 0 && (
          <ChipGroup>
            {PERIODS.filter((p) => availForBet.includes(p.id)).map((p) => (
              <Chip key={p.id} active={effectivePeriod === p.id} onClick={() => setSelectedPeriod(p.id)}>
                {p.label}
              </Chip>
            ))}
          </ChipGroup>
        )}
        <div className="ml-auto">
          <ChipGroup>
            <Chip active={hideNoVol} onClick={() => setHideNoVol((v) => !v)}>
              Hide 0-Vol
            </Chip>
            <Chip active={hideSettled} onClick={() => setHideSettled((v) => !v)}>
              Hide settled
            </Chip>
          </ChipGroup>
        </div>
      </div>

      {/* Same table as the moneyline, minus the Time column. The Odds column
          stays: it's where the expanded ladder drops your resting orders. With
          no 1H consensus it just reads "—" in the summary rows (as moneyline
          does whenever consensus is absent). */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <Th className="w-60">Bet</Th>
              <Th className="w-16 text-center" title="Consensus odds (full-game only) — also hosts your resting orders in the ladder">Odds</Th>
              <Th className="w-32 text-center">Kalshi</Th>
              <Th className="w-16 text-right" title="Total BID/ASK contracts">K Vol</Th>
              <Th className="w-24 text-right" title="Total $ resting in book">K Liq</Th>
              <Th className="w-16 text-right" title={`Best-bid depth on the active ${marketSide.toUpperCase()} side`}>K Q</Th>
              <Th className="w-16 text-right" title="Kalshi best-bid depth change over the last 1m">K Δ/m</Th>
              <Th className="w-32 text-center">Poly</Th>
              <Th className="w-16 text-right" title="Total BID/ASK contracts">P Vol</Th>
              <Th className="w-24 text-right" title="Total $ resting in book">P Liq</Th>
              <Th className="w-16 text-right" title="Polymarket best-bid depth change over the last 1m">P Δ/m</Th>
            </tr>
          </thead>
          <tbody>
            {/* Map over the FULL rows (keeps i = line index for the panel
                context); skip no-volume rows when "Hide 0-Vol" is on, and
                settled rows when "Hide settled" is on (otherwise they render
                dimmed with a SETTLED chip). */}
            {visibleRows.map((r, i) => {
              if (!r.visible) return null;
              const isTie = !!r.continuation;
              return (
              <GameRows
                key={r.synth.key}
                game={r.synth}
                gameIndex={isTie ? Math.max(0, i - 1) : i}
                continuation={isTie && !!mlPair?.visible}
                groupSelected={isTie ? mlPairExpanded : mlTieExpanded}
                groupExtendsBelow={!isTie && !!mlTie?.visible}
                settled={r.settled}
                marketSide={marketSide}
                priceMode={priceMode}
                expandedTeamSide={expanded?.key === r.synth.key ? expanded.side : null}
                onToggleExpand={(key, side) => setExpanded((e) => (e?.key === key && e?.side === side ? null : { key, side }))}
                onTradeClick={(_key, side, venue, opts) => {
                  // Clicking a YES/NO pill flips the whole table (and the toolbar
                  // toggle) to that side — marketSide drives every GameRows here.
                  if (opts?.initialOrderSide) setMarketSide(opts.initialOrderSide);
                  // Drive the panel's bet-line context: spread/total rows map 1:1
                  // to the period's line array (row i = line i), so the panel gets
                  // the "Over X" / "wins by over X" layout. Moneyline → null.
                  onPanelLineChange?.(
                    effectiveBetType === 'spread' || effectiveBetType === 'total'
                      ? { kind: effectiveBetType, idx: i }
                      : null,
                  );
                  onPillClick({
                    game: r.synth,
                    teamSide: side,
                    venue,
                    prefillPrice: opts?.priceCents,
                    initialOrderSide: opts?.initialOrderSide,
                    postOnly: opts?.postOnly ?? false,
                  });
                }}
                positionByTicker={positionByTicker}
                ordersByTicker={ordersByTicker}
                hideDateColumn
                hideScoreColumn
                singleSide={r.singleSide}
                labelMaxWidthClass="max-w-[200px]"
              />
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
